import { useEffect, useState } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import AppShell from "../layout/AppShell";
import LoginPage from "../pages/LoginPage";
import DashboardPage from "../pages/DashboardPage";
import CallsPage from "../pages/CallsPage";
import AnalyticsPage from "../pages/AnalyticsPage";
import LeadManagementPage from "../pages/LeadManagementPage";
import AppointmentsPage from "../pages/AppointmentsPage";
import CampaignsPage from "../pages/CampaignsPage";
import TeamManagementPage from "../pages/TeamManagementPage";
import SettingsPage from "../pages/SettingsPage";
import LoadingState from "../components/common/LoadingState";
import { getSettingsData } from "../services/settingsApi";
import { NAV_ITEMS } from "../utils/constants";
import { canAccessPage } from "../utils/roleAccess";
import ProtectedRoute from "./ProtectedRoute";
import PublicRoute from "./PublicRoute";

const PAGE_PATHS = NAV_ITEMS.reduce((paths, item) => {
  paths[item.id] = item.to;
  return paths;
}, {});

function resolveDefaultPath(settings, user) {
  const pageId = String(settings?.account?.defaultDashboard || "dashboard").trim();

  if (pageId && PAGE_PATHS[pageId] && canAccessPage(user, pageId)) {
    return PAGE_PATHS[pageId];
  }

  return "/dashboard";
}

function RootRedirect() {
  const location = useLocation();
  const { initializing, isAuthenticated, user } = useAuth();
  const [targetPath, setTargetPath] = useState("");

  useEffect(() => {
    let active = true;

    if (!isAuthenticated || initializing) {
      setTargetPath("");
      return undefined;
    }

    getSettingsData()
      .then((settings) => {
        if (active) {
          setTargetPath(resolveDefaultPath(settings, user));
        }
      })
      .catch(() => {
        if (active) {
          setTargetPath("/dashboard");
        }
      });

    return () => {
      active = false;
    };
  }, [initializing, isAuthenticated, user]);

  if (initializing) {
    return (
      <LoadingState
        compact
        title="Loading session..."
        subtitle="Checking your access."
      />
    );
  }

  if (isAuthenticated) {
    if (!targetPath) {
      return (
        <LoadingState
          compact
          title="Loading workspace..."
          subtitle="Opening your default dashboard."
        />
      );
    }

    return <Navigate to={`${targetPath}${location.search || ""}`} replace />;
  }

  return <Navigate to="/login" replace />;
}

export default function AppRouter() {
  return (
    <Routes>
      <Route path="/" element={<RootRedirect />} />
      <Route element={<PublicRoute />}>
        <Route path="/login" element={<LoginPage />} />
      </Route>
      <Route element={<ProtectedRoute />}>
        <Route element={<AppShell />}>
          <Route element={<ProtectedRoute pageId="dashboard" />}>
            <Route path="/dashboard" element={<DashboardPage />} />
          </Route>
          <Route element={<ProtectedRoute pageId="calls" />}>
            <Route path="/calls" element={<CallsPage />} />
          </Route>
          <Route element={<ProtectedRoute pageId="analytics" />}>
            <Route path="/analytics" element={<AnalyticsPage />} />
          </Route>
          <Route element={<ProtectedRoute pageId="lead-management" />}>
            <Route path="/lead-management" element={<LeadManagementPage />} />
          </Route>
          <Route element={<ProtectedRoute pageId="appointments" />}>
            <Route path="/appointments" element={<AppointmentsPage />} />
          </Route>
          <Route element={<ProtectedRoute pageId="campaigns" />}>
            <Route path="/campaigns" element={<CampaignsPage />} />
          </Route>
          <Route element={<ProtectedRoute pageId="team-management" />}>
            <Route path="/team-management" element={<TeamManagementPage />} />
          </Route>
          <Route element={<ProtectedRoute pageId="settings" />}>
            <Route path="/settings" element={<SettingsPage />} />
          </Route>
        </Route>
      </Route>
      <Route path="*" element={<RootRedirect />} />
    </Routes>
  );
}
